import { createFileRoute, Link } from "@tanstack/react-router";
import useRequest from "@/lib/request/useRequest";
import useFeatureFlag from "@/lib/featureFlags/useFeatureFlags";
import useAuth from "@/lib/auth/useAuth";
import { useMemo, useState, useEffect, useCallback, useRef } from "react";
import { Organization } from "@/types/organization";
import { User } from "@/types/user";
import { NaveField } from "@/types/water";
import { EnrollmentTier } from "@/lib/utils/enrollment";
import { SortingState, VisibilityState } from "@tanstack/react-table";
import { FieldFilterBar } from "@/components/fields/FieldFilterBar";
import {
    Dropdown,
    DropdownTrigger,
    DropdownMenu,
    DropdownItem,
    Button,
    Divider,
    addToast,
    Modal,
    ModalContent,
    ModalBody,
    Chip,
} from "@heroui/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faBars,
    faFill,
    faLeaf,
    faUpRightFromSquare,
    faPenToSquare,
    faSquareBinary,
    faFileZipper,
    faArrowUpFromGroundWater,
    faClipboardList,
    faChevronDown,
    faExclamationCircle,
} from "@fortawesome/free-solid-svg-icons";
import { FieldInsights } from "@/types/fieldInsights";
import { ColumnDef } from "@tanstack/react-table";
import {
    createFieldColumns,
    FieldColumnOptions,
} from "@/components/fields/fieldColumns";
import TableV2 from "@/components/table/TableV2";
import { useInfiniteQuery } from "@tanstack/react-query";
import { PaginationResponse } from "@/types/pagination";
import BulkEditNitrogenPlan from "@/components/home/BulkEditNitrogenPlan";
import BulkRxExport from "@/components/home/BulkRxExport";
import NStatusChip from "@/components/common/NStatusChip";
import RecommendationChip from "@/components/common/RecommendationChip";
import BulkSetGrowthStage from "@/components/home/BulkSetGrowthStage";
import UserRecommendationForm from "@/components/field/UserRecommendationForm";
import { CropConfiguration } from "@/types/cropConfiguration";
import { FertilizerProduct } from "@/types/products";
import { roundTo } from "@/lib/utils/numbers";
import { useI18n } from "@/lib/i18n/useI18n";
import { getSimulationTypeDisplayName } from "@/types/nitrogenPlan";
import FieldNitrogenAdvisor from "@/components/field/FieldNitrogenAdvisor";
import { zodValidator } from "@tanstack/zod-adapter";
import { z } from "zod";
import SIPreview from "@/components/home/SIPreview";
import BulkLogIrrigation from "@/components/home/BulkLogIrrigation";
import BulkLogApplication from "@/components/home/BulkLogApplication";
import classNames from "classnames";

const searchSchema = z.object({
    search: z.string().optional(),
    ordering: z.string().optional(),
    service_provider: z.string().optional(),
    grower: z.string().optional(),
    account_manager: z.string().optional(),
    date: z.string().optional(),
    advisor_field: z.number().optional(),
    columns: z.string().optional(),
});

export const Route = createFileRoute("/dashboard/home/insights")({
    component: RouteComponent,
    validateSearch: zodValidator(searchSchema),
});

const COLUMN_VISIBILITY_STORAGE_KEY = "insights-column-visibility";

const DEFAULT_COLUMN_VISIBILITY: VisibilityState = {
    selection: true,
    grower: true,
    name: true,
    irrigation: true,
    recommendation_date: true,
    sufficiency: true,
    rate: true,
    product: true,
    model: true,
    openAdvisor: true,
    totalIrrigation: true,
    h20Sufficiency: true,
    currentWaterDeficit: true,
    forecastWaterDeficit: true,
    forecastRain: true,
};

const VALID_COLUMN_IDS = new Set(Object.keys(DEFAULT_COLUMN_VISIBILITY));

function parseColumnsFromUrl(
    columnsParam: string | undefined,
): VisibilityState {
    if (!columnsParam?.trim()) {
        return { ...DEFAULT_COLUMN_VISIBILITY };
    }
    const visibleIds = columnsParam
        .split(",")
        .map((id) => id.trim())
        .filter((id) => VALID_COLUMN_IDS.has(id));
    const visibility = { ...DEFAULT_COLUMN_VISIBILITY };
    for (const id of Object.keys(visibility)) {
        (visibility as Record<string, boolean>)[id] = visibleIds.includes(id);
    }
    return visibility;
}

function visibilityToColumnParam(visibility: VisibilityState): string {
    return Object.entries(visibility)
        .filter(([, visible]) => visible)
        .map(([id]) => id)
        .join(",");
}

function parseColumnsFromStorage(
    stored: string | null,
): VisibilityState | null {
    try {
        if (!stored) return null;
        const parsed = JSON.parse(stored) as Record<string, boolean>;
        return { ...DEFAULT_COLUMN_VISIBILITY, ...parsed };
    } catch {
        return null;
    }
}

function RouteComponent() {
    const { useGetRequest, axiosRequest, useMutateRequest } = useRequest();

    const {
        search,
        ordering,
        advisor_field: advisorField,
        service_provider,
        grower,
        account_manager,
        date,
        columns: columnsParam,
    } = Route.useSearch();

    const navigate = Route.useNavigate();

    const { formatDateMMDDYYYY, formatUnit } = useI18n();

    const { customRecommendationsEnabled } = useFeatureFlag();

    const { isConsultantOrSentinel } = useAuth();

    const serviceProvidersQuery = useGetRequest<Organization[]>({
        url: "organizations/service_providers/",
    });

    const growersQuery = useGetRequest<Organization[]>({
        url: "organizations/farms/",
    });

    const accountManagersQuery = useGetRequest<User[]>({
        url: "users/account_managers/",
    });

    const cropsQuery = useGetRequest<CropConfiguration[]>({
        url: "crops/",
    });

    const fertilizersQuery = useGetRequest<FertilizerProduct[]>({
        url: "fertilizers/",
    });

    const growersById = useMemo(() => {
        return (
            growersQuery.data?.reduce(
                (acc, grower) => {
                    acc[grower.id] = grower;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [growersQuery.data]);

    const serviceProvidersById = useMemo(() => {
        return (
            serviceProvidersQuery.data?.reduce(
                (acc, serviceProvider) => {
                    acc[serviceProvider.id] = serviceProvider;
                    return acc;
                },
                {} as Record<number, Organization>,
            ) ?? {}
        );
    }, [serviceProvidersQuery.data]);

    const accountManagersById = useMemo(() => {
        return (
            accountManagersQuery.data?.reduce(
                (acc, accountManager) => {
                    acc[accountManager.id] = accountManager;
                    return acc;
                },
                {} as Record<number, User>,
            ) ?? {}
        );
    }, [accountManagersQuery.data]);

    const growerOptions = useMemo(() => {
        return (
            growersQuery.data?.map((grower) => ({
                label: grower.name,
                key: grower.id.toString(),
            })) ?? []
        );
    }, [growersQuery.data]);

    const serviceProviderOptions = useMemo(() => {
        return (
            serviceProvidersQuery.data?.map((serviceProvider) => ({
                label: serviceProvider.name,
                key: serviceProvider.id.toString(),
            })) ?? []
        );
    }, [serviceProvidersQuery.data]);

    const fertilizersById = useMemo(() => {
        return (
            fertilizersQuery.data?.reduce(
                (acc, fertilizer) => {
                    acc[fertilizer.id] = fertilizer;
                    return acc;
                },
                {} as Record<number, FertilizerProduct>,
            ) ?? {}
        );
    }, [fertilizersQuery.data]);

    const [sorting, setSorting] = useState<SortingState>([
        ordering
            ? {
                  id: ordering,
                  desc: ordering.startsWith("-"),
              }
            : {
                  id: "sufficiency",
                  desc: false,
              },
    ]);

    useEffect(() => {
        if (sorting.length > 0) {
            navigate({
                search: (prev) => ({
                    ...prev,
                    ordering: `${sorting[0].desc ? "-" : ""}${sorting[0].id}`,
                }),
            });
        } else {
            navigate({
                search: (prev) => ({ ...prev, ordering: "" }),
            });
        }
    }, [sorting, navigate]);

    const query = useInfiniteQuery<PaginationResponse<FieldInsights>>({
        queryKey: [
            "field_nitrogen_plans",
            search,
            ordering,
            service_provider,
            grower,
            account_manager,
            date,
        ],
        getNextPageParam: (lastPage) => {
            return lastPage.next ? lastPage.page + 1 : undefined;
        },
        getPreviousPageParam: (firstPage) => {
            return firstPage.previous ? firstPage.page - 1 : undefined;
        },
        initialPageParam: 1,
        queryFn: async ({ pageParam = 1 }) => {
            return await axiosRequest<PaginationResponse<FieldInsights>>({
                url: "fields/insights/",
                params: {
                    search,
                    ordering: ordering === "" ? "sufficiency" : ordering,
                    service_provider,
                    grower,
                    account_manager,
                    page: pageParam,
                },
            });
        },
    });

    const insightsData = useMemo(() => {
        return query.data?.pages.flatMap((page) => page.results) ?? [];
    }, [query.data]);

    const fieldsHaveWaterInsights = useMemo(() => {
        return insightsData.some(
            (field) =>
                field.active_enrollment?.enrollment_tier ===
                    EnrollmentTier.Irrigation ||
                field.active_enrollment?.enrollment_tier ===
                    EnrollmentTier.Complete,
        );
    }, [insightsData]);

    const waterQuery = useGetRequest<NaveField[]>({
        url: "fields/insights/water/",
        enabled: fieldsHaveWaterInsights,
    });

    const tableData = useMemo(() => {
        return insightsData.map((field) => ({
            ...field,
            water: waterQuery.data?.find((water) => water.id === field.id),
        }));
    }, [insightsData, waterQuery.data]);

    const [loadingRx, setLoadingRx] = useState(false);

    const setOpenAdvisorFieldId = useCallback(
        (fieldId: number | undefined) => {
            navigate({
                search: (prev) => ({
                    ...prev,
                    advisor_field: fieldId,
                }),
            });
        },
        [navigate],
    );

    const insightsColumns: ColumnDef<FieldInsights>[] = useMemo(() => {
        const columnOptions: FieldColumnOptions = {
            growersById,
            serviceProvidersById,
            accountManagersById,
        };

        return [
            createFieldColumns.selection<FieldInsights>(),
            createFieldColumns.growerAndServiceProvider<FieldInsights>(
                columnOptions,
            ),
            createFieldColumns.fieldName<FieldInsights>(),
            createFieldColumns.irrigation<FieldInsights>(),
            // {
            //     id: "growthStage",
            //     header: "Growth Stage",
            //     accessorFn: (row) => row.growth_stage,
            //     size: 125,
            // },
            {
                id: "recommendation_date",
                header: "Image Date",
                accessorFn: (row) => {
                    return (
                        row.recommendation?.user_recommendation
                            ?.recommendation_date ??
                        row.recommendation?.recommendation_date ??
                        undefined
                    );
                },
                cell: (info) => {
                    if (!info.getValue()) {
                        return null;
                    }
                    return (
                        <div className="text-sm">
                            {formatDateMMDDYYYY(info.getValue() as string)}
                        </div>
                    );
                },
                size: 90,
            },
            // {
            //     id: 'nApplied',
            //     header: 'N Applied',
            //     size: 100,
            // },
            {
                id: "sufficiency",
                header: "N Sufficiency",
                accessorFn: (row) => row.recommendation.si_mean,
                cell: ({ row }) => {
                    // link to field insights dashboard
                    return (
                        <div className="flex flex-col gap-1 mx-1">
                            <div className="w-full">
                                <RecommendationChip
                                    recommendation={row.original.recommendation}
                                    notes={
                                        row.original.recommendation
                                            .user_recommendation?.notes ??
                                        undefined
                                    }
                                    user={
                                        row.original.recommendation
                                            .user_recommendation?.user ??
                                        undefined
                                    }
                                    deferDoNotApply={true}
                                    fullWidth={true}
                                />
                            </div>
                            {row.original.recommendation?.si_mean && (
                                <div className="w-full">
                                    <NStatusChip
                                        nStatus={
                                            row.original.recommendation.n_status
                                        }
                                        sufficiency={roundTo(
                                            row.original.recommendation
                                                .si_mean * 100,
                                            1,
                                        )}
                                        fullWidth={true}
                                    />
                                </div>
                            )}
                        </div>
                    );
                },
                size: 150,
            },
            {
                id: "rate",
                header: "Rate",
                size: 100,
                accessorFn: (row) =>
                    row.default_nitrogen_plan_simulation?.current_rate,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-row gap-1 items-center justify-center mx-2">
                            {row.original.default_nitrogen_plan_simulation
                                ?.initial_rate && (
                                <Button
                                    color="primary"
                                    size="sm"
                                    variant="flat"
                                    radius="sm"
                                    isDisabled={loadingRx}
                                    fullWidth
                                    endContent={
                                        <FontAwesomeIcon
                                            icon={faUpRightFromSquare}
                                        />
                                    }
                                    onPress={() => {
                                        const fieldId = row.original.id;
                                        const planId =
                                            row.original
                                                .default_nitrogen_plan_simulation
                                                ?.field_nitrogen_plan;
                                        const simulationId =
                                            row.original
                                                .default_nitrogen_plan_simulation
                                                ?.id;
                                        if (!planId || !simulationId) {
                                            return;
                                        }
                                        setLoadingRx(true);
                                        axiosRequest<{
                                            prescription_id: number;
                                            prescription_name: string;
                                        }>({
                                            url: `field_nitrogen_plans/${planId}/simulations/${simulationId}/prescription/`,
                                            method: "GET",
                                        })
                                            .then((response) => {
                                                const prescriptionId =
                                                    response.prescription_id;
                                                window.open(
                                                    `/dashboard/fields/${fieldId}/rx/${
                                                        prescriptionId
                                                    }`,
                                                    "_blank",
                                                );
                                            })
                                            .catch(() => {
                                                addToast({
                                                    title: "Failed to fetch prescription",
                                                    color: "danger",
                                                });
                                            })
                                            .finally(() => {
                                                setLoadingRx(false);
                                            });
                                    }}
                                >
                                    {row.original
                                        .default_nitrogen_plan_simulation
                                        ?.current_rate ?? null}
                                </Button>
                            )}
                        </div>
                    );
                },
            },
            // {
            //     id: "method",
            //     header: "Method",
            //     size: 100,
            // },
            {
                id: "product",
                header: "Product",
                size: 130,
                accessorFn: (row) =>
                    row.default_nitrogen_plan_simulation?.product,
                cell: ({ row }) => {
                    return (
                        <div className="text-sm">
                            {row.original.default_nitrogen_plan_simulation
                                ?.product
                                ? fertilizersById[
                                      row.original
                                          .default_nitrogen_plan_simulation
                                          .product
                                  ]?.name
                                : null}
                        </div>
                    );
                },
                enableSorting: false,
            },
            {
                id: "model",
                header: "Model",
                size: 120,
                accessorFn: (row) =>
                    row.default_nitrogen_plan_simulation?.simulation_type,
                cell: ({ row }) => {
                    const simulationType =
                        row.original.default_nitrogen_plan_simulation
                            ?.simulation_type;
                    return (
                        <div className="text-sm">
                            {simulationType
                                ? getSimulationTypeDisplayName(simulationType)
                                : null}
                        </div>
                    );
                },
                enableSorting: false,
            },
            {
                id: "openAdvisor",
                header: "Advisor",
                accessorFn: (row) => row.id,
                cell: ({ row }) => {
                    return (
                        <div>
                            <Button
                                variant="flat"
                                size="sm"
                                color="primary"
                                onPress={() =>
                                    setOpenAdvisorFieldId(row.original.id)
                                }
                                startContent={<FontAwesomeIcon icon={faLeaf} />}
                            >
                                Advisor
                            </Button>
                        </div>
                    );
                },
                size: 100,
            },
        ];
    }, [
        growersById,
        serviceProvidersById,
        accountManagersById,
        formatDateMMDDYYYY,
        loadingRx,
        axiosRequest,
        fertilizersById,
        setOpenAdvisorFieldId,
    ]);

    const waterColumns: ColumnDef<FieldInsights>[] = useMemo(
        () => [
            {
                id: "h2oSufficiency",
                header: "H2O Sufficiency",
                accessorFn: (row) =>
                    row.water?.water_estimate?.capacity_percentage,
                cell: ({ row }) => {
                    return (
                        <div className="flex flex-col gap-1 mx-1 items-center">
                            <div className="w-full">
                                {row.original.water?.water_estimate
                                    ?.moisture_status ==
                                    "Nearing Depletion" && (
                                    <Chip
                                        size="sm"
                                        radius="sm"
                                        variant="flat"
                                        classNames={{
                                            base: "px-2 text-red-700 bg-red-100 w-full max-w-full flex justify-center",
                                            content:
                                                "font-bold w-full text-center",
                                        }}
                                    >
                                        <FontAwesomeIcon
                                            icon={faExclamationCircle}
                                            className="mr-1"
                                        />
                                        Irrigate
                                    </Chip>
                                )}
                            </div>
                            <div className="w-full">
                                <Chip
                                    size="sm"
                                    radius="sm"
                                    variant="flat"
                                    classNames={{
                                        base: classNames(
                                            "px-2 w-full max-w-full flex justify-center",
                                            {
                                                "bg-green-100 text-green-700":
                                                    row.original.water
                                                        ?.water_estimate
                                                        ?.moisture_status ===
                                                    "Optimal",
                                                "bg-blue-100 text-blue-700":
                                                    row.original.water
                                                        ?.water_estimate
                                                        ?.moisture_status ===
                                                    "Nearing Saturation",
                                                "bg-yellow-100 text-yellow-700":
                                                    row.original.water
                                                        ?.water_estimate
                                                        ?.moisture_status ===
                                                    "Nearing Depletion",
                                            },
                                        ),
                                        content: "font-bold w-full text-center",
                                    }}
                                >
                                    {row.original.water?.water_estimate?.capacity_percentage?.toFixed(
                                        1,
                                    ) ?? "-"}{" "}
                                    %
                                </Chip>
                            </div>
                        </div>
                    );
                },
                size: 100,
            },
            {
                id: "currentWaterDeficit",
                header: "Current Deficit",
                accessorFn: (row) => row.water?.water_estimate?.deficit,
                cell: ({ row }) => {
                    return row.original.water ? (
                        <Chip
                            size="sm"
                            radius="sm"
                            classNames={{
                                base: classNames("px-2", {
                                    "bg-green-100 text-green-700":
                                        row.original.water?.water_estimate
                                            ?.deficit > 0,
                                    "bg-yellow-100 text-yellow-700":
                                        row.original.water?.water_estimate
                                            ?.deficit <= 0,
                                }),
                                content: "font-bold",
                            }}
                        >
                            {row.original.water?.water_estimate?.deficit > 0
                                ? "+"
                                : ""}{" "}
                            {row.original.water?.water_estimate?.deficit?.toFixed(
                                2,
                            ) ?? "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    ) : (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color="default"
                            classNames={{ base: "px-2", content: "font-bold" }}
                        >
                            -
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "forecastWaterDeficit",
                header: "7 Day Deficit Forecast",
                accessorFn: (row) =>
                    row.water?.water_estimate?.deficit_forecast,
                cell: ({ row }) => {
                    return row.original.water ? (
                        <Chip
                            size="sm"
                            radius="sm"
                            classNames={{
                                base: classNames("px-2", {
                                    "bg-green-100 text-green-700":
                                        row.original.water?.water_estimate
                                            ?.deficit_forecast > 0,
                                    "bg-yellow-100 text-yellow-700":
                                        row.original.water?.water_estimate
                                            ?.deficit_forecast <= 0,
                                }),
                                content: "font-bold",
                            }}
                        >
                            {row.original.water?.water_estimate
                                ?.deficit_forecast > 0
                                ? "+"
                                : ""}{" "}
                            {row.original.water?.water_estimate?.deficit_forecast?.toFixed(
                                2,
                            ) ?? "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    ) : (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color="default"
                            classNames={{ base: "px-2", content: "font-bold" }}
                        >
                            -
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "totalIrrigation",
                header: "Total Irrigation",
                accessorFn: (row) => row.water?.cumulativeIrrigation,
                cell: ({ row }) => {
                    return (
                        <Chip
                            radius="sm"
                            as={Link}
                            to={`/dashboard/fields/${row.original.id}/operations`}
                            target="_blank"
                            variant="flat"
                            className="text-xs px-2"
                            color={row.original.water ? "primary" : "default"}
                            classNames={{ content: "font-bold" }}
                            endContent={
                                <FontAwesomeIcon
                                    icon={faUpRightFromSquare}
                                    size="sm"
                                    className="mr-2"
                                />
                            }
                        >
                            {row.original.water?.cumulativeIrrigation?.toFixed(
                                2,
                            ) ?? "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    );
                },
                size: 100,
            },
            {
                id: "forecastRain",
                header: "Rain Forecast",
                accessorFn: (row) => row.water?.expectedRain,
                cell: ({ row }) => {
                    return (
                        <Chip
                            size="sm"
                            radius="sm"
                            variant="flat"
                            color={
                                row.original.water?.expectedRain
                                    ? "primary"
                                    : "default"
                            }
                            classNames={{ base: "px-2", content: "font-bold" }}
                        >
                            {row.original.water?.expectedRain?.toFixed(2) ??
                                "-"}{" "}
                            {formatUnit("in", "mm")}
                        </Chip>
                    );
                },
                size: 100,
            },
        ],
        [formatUnit],
    );

    const columns = useMemo(() => {
        return fieldsHaveWaterInsights
            ? [...insightsColumns, ...waterColumns]
            : insightsColumns;
    }, [insightsColumns, waterColumns, fieldsHaveWaterInsights]);

    const [selectedFields, setSelectedFields] = useState<FieldInsights[]>([]);

    const handleSelectionChange = (selectedData: FieldInsights[]) => {
        setSelectedFields(selectedData);
    };

    const selectedFieldsIdsByCropConfiguration = useMemo(() => {
        const mapping: Record<number, number[]> = {};
        selectedFields.forEach((field) => {
            const cropConfigId =
                field.active_planting_operation.crop_configuration.id;
            if (!mapping[cropConfigId]) {
                mapping[cropConfigId] = [];
            }
            mapping[cropConfigId].push(field.id!);
        });
        return mapping;
    }, [selectedFields]);

    const isLoading = useMemo(() => {
        return (
            serviceProvidersQuery.isLoading ||
            growersQuery.isLoading ||
            accountManagersQuery.isLoading ||
            query.isLoading
        );
    }, [
        serviceProvidersQuery.isLoading,
        growersQuery.isLoading,
        accountManagersQuery.isLoading,
        query.isLoading,
    ]);

    const [bulkEditAction, setBulkEditAction] = useState<
        "edit-rate" | "set-product" | "set-method" | "set-model" | null
    >(null);
    const [exportRxOpen, setExportRxOpen] = useState(false);
    const [bulkSetGrowthStageOpen, setBulkSetGrowthStageOpen] = useState(false);
    const [editRecommendationOpen, setEditRecommendationOpen] = useState(false);
    const prescriptionsQuery = useMutateRequest<
        {
            field_id: number;
            field_name: string;
            simulation_id: number;
            prescription_id: number;
            prescription_name: string;
        }[]
    >({
        url: "field_nitrogen_plans/simulations_prescriptions/",
        method: "put",
    });
    const [prescriptionsToExport, setPrescriptionsToExport] = useState<
        {
            field_id: number;
            field_name: string;
            simulation_id: number;
            prescription_id: number;
            prescription_name: string;
        }[]
    >([]);
    const [bulkLogIrrigationOpen, setBulkLogIrrigationOpen] = useState(false);
    const [bulkLogApplicationOpen, setBulkLogApplicationOpen] = useState(false);

    const selectedField = useMemo(() => {
        return tableData.find(
            (field) => field.id.toString() === advisorField?.toString(),
        );
    }, [tableData, advisorField]);

    const loadedFromStorageRef = useRef(false);

    const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(
        () => {
            if (columnsParam?.trim()) {
                return parseColumnsFromUrl(columnsParam);
            }
            const stored = localStorage.getItem(COLUMN_VISIBILITY_STORAGE_KEY);
            const fromStorage = parseColumnsFromStorage(stored);
            if (fromStorage) {
                loadedFromStorageRef.current = true;
                return fromStorage;
            }
            return { ...DEFAULT_COLUMN_VISIBILITY };
        },
    );

    const handleColumnVisibilityChange = (
        updater: VisibilityState | ((old: VisibilityState) => VisibilityState),
    ) => {
        setColumnVisibility((prev) => {
            const next =
                typeof updater === "function" ? updater(prev) : updater;
            localStorage.setItem(
                COLUMN_VISIBILITY_STORAGE_KEY,
                JSON.stringify(next),
            );
            navigate({
                search: (prevSearch) => ({
                    ...prevSearch,
                    columns: visibilityToColumnParam(next),
                }),
                replace: true,
            });
            return next;
        });
    };

    useEffect(() => {
        if (loadedFromStorageRef.current && !columnsParam?.trim()) {
            loadedFromStorageRef.current = false;
            navigate({
                search: (prev) => ({
                    ...prev,
                    columns: visibilityToColumnParam(columnVisibility),
                }),
                replace: true,
            });
        }
    }, [columnVisibility, columnsParam, navigate]);

    const selectedColumns = useMemo(() => {
        return Object.keys(columnVisibility).filter(
            (key) => columnVisibility[key as keyof VisibilityState],
        );
    }, [columnVisibility]);

    return (
        <div className="h-full flex flex-col overflow-hidden">
            <div className="flex flex-row gap-2 p-2 items-center justify-between w-full">
                <div className="flex flex-row gap-2 items-center">
                    <FieldFilterBar
                        searchValue={search}
                        onSearchChange={(value) => {
                            navigate({
                                search: (prev) => ({ ...prev, search: value }),
                            });
                        }}
                        serviceProviderId={service_provider}
                        onServiceProviderChange={(id) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    service_provider: id ?? "",
                                }),
                            });
                        }}
                        serviceProviderOptions={serviceProviderOptions}
                        growerId={grower}
                        onGrowerChange={(id) => {
                            navigate({
                                search: (prev) => ({
                                    ...prev,
                                    grower: id ?? "",
                                }),
                            });
                        }}
                        growerOptions={growerOptions}
                    />
                    <Dropdown>
                        <DropdownTrigger>
                            <Button size="sm">
                                Columns <FontAwesomeIcon icon={faChevronDown} />
                            </Button>
                        </DropdownTrigger>
                        <DropdownMenu
                            disallowEmptySelection
                            closeOnSelect={false}
                            selectionMode="multiple"
                            selectedKeys={selectedColumns}
                        >
                            <DropdownItem
                                key="grower"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        grower: !prev.grower,
                                    }))
                                }
                            >
                                Farm/CSP
                            </DropdownItem>
                            <DropdownItem
                                key="name"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        name: !prev.name,
                                    }))
                                }
                            >
                                Name
                            </DropdownItem>
                            <DropdownItem
                                key="irrigation"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        irrigation: !prev.irrigation,
                                    }))
                                }
                            >
                                Irrigation
                            </DropdownItem>
                            <DropdownItem
                                key="recommendation_date"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        recommendation_date:
                                            !prev.recommendation_date,
                                    }))
                                }
                            >
                                Image Date
                            </DropdownItem>
                            <DropdownItem
                                key="sufficiency"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        sufficiency: !prev.sufficiency,
                                    }))
                                }
                            >
                                Sufficiency
                            </DropdownItem>
                            <DropdownItem
                                key="rate"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        rate: !prev.rate,
                                    }))
                                }
                            >
                                Rate
                            </DropdownItem>
                            <DropdownItem
                                key="product"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        product: !prev.product,
                                    }))
                                }
                            >
                                Product
                            </DropdownItem>
                            <DropdownItem
                                key="model"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        model: !prev.model,
                                    }))
                                }
                            >
                                Model
                            </DropdownItem>
                            <DropdownItem
                                key="openAdvisor"
                                onPress={() =>
                                    handleColumnVisibilityChange((prev) => ({
                                        ...prev,
                                        openAdvisor: !prev.openAdvisor,
                                    }))
                                }
                            >
                                Advisor
                            </DropdownItem>
                            {fieldsHaveWaterInsights ? (
                                <>
                                    <DropdownItem
                                        key="totalIrrigation"
                                        onPress={() =>
                                            handleColumnVisibilityChange(
                                                (prev) => ({
                                                    ...prev,
                                                    totalIrrigation:
                                                        !prev.totalIrrigation,
                                                }),
                                            )
                                        }
                                    >
                                        Total Irrigation
                                    </DropdownItem>
                                    <DropdownItem
                                        key="h20Sufficiency"
                                        onPress={() =>
                                            handleColumnVisibilityChange(
                                                (prev) => ({
                                                    ...prev,
                                                    h20Sufficiency:
                                                        !prev.h20Sufficiency,
                                                }),
                                            )
                                        }
                                    >
                                        H20 Sufficiency
                                    </DropdownItem>
                                    <DropdownItem
                                        key="currentWaterDeficit"
                                        onPress={() =>
                                            handleColumnVisibilityChange(
                                                (prev) => ({
                                                    ...prev,
                                                    currentWaterDeficit:
                                                        !prev.currentWaterDeficit,
                                                }),
                                            )
                                        }
                                    >
                                        Current Deficit
                                    </DropdownItem>
                                    <DropdownItem
                                        key="forecastWaterDeficit"
                                        onPress={() =>
                                            handleColumnVisibilityChange(
                                                (prev) => ({
                                                    ...prev,
                                                    forecastWaterDeficit:
                                                        !prev.forecastWaterDeficit,
                                                }),
                                            )
                                        }
                                    >
                                        Forecast Water Deficit
                                    </DropdownItem>
                                    <DropdownItem
                                        key="forecastRain"
                                        onPress={() =>
                                            handleColumnVisibilityChange(
                                                (prev) => ({
                                                    ...prev,
                                                    forecastRain:
                                                        !prev.forecastRain,
                                                }),
                                            )
                                        }
                                    >
                                        Forecast Rain
                                    </DropdownItem>
                                </>
                            ) : null}
                        </DropdownMenu>
                    </Dropdown>
                </div>
                <div className="flex flex-row gap-2 items-center">
                    <Dropdown>
                        <DropdownTrigger>
                            <Button
                                color="primary"
                                size="sm"
                                startContent={<FontAwesomeIcon icon={faBars} />}
                                isDisabled={selectedFields.length === 0}
                            >
                                Actions
                            </Button>
                        </DropdownTrigger>
                        <DropdownMenu>
                            {customRecommendationsEnabled &&
                            isConsultantOrSentinel ? (
                                <DropdownItem
                                    key="edit-recommendation"
                                    onPress={() =>
                                        setEditRecommendationOpen(true)
                                    }
                                    startContent={
                                        <FontAwesomeIcon
                                            icon={faPenToSquare}
                                            className="text-primary-500"
                                        />
                                    }
                                >
                                    Edit Recommendation
                                </DropdownItem>
                            ) : null}
                            <DropdownItem
                                key="edit-rate"
                                onPress={() => setBulkEditAction("edit-rate")}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faPenToSquare}
                                        className="text-primary-500"
                                    />
                                }
                            >
                                Edit Rate
                            </DropdownItem>
                            <DropdownItem
                                key="set-product"
                                onPress={() => setBulkEditAction("set-product")}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faFill}
                                        className="text-primary-500"
                                    />
                                }
                            >
                                Edit Product
                            </DropdownItem>
                            <DropdownItem
                                key="set-model"
                                onPress={() => setBulkEditAction("set-model")}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faSquareBinary}
                                        className="text-primary-500"
                                    />
                                }
                            >
                                Edit Model
                            </DropdownItem>
                            <DropdownItem
                                key="set-growth-stage"
                                onPress={() => setBulkSetGrowthStageOpen(true)}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faLeaf}
                                        className="text-primary-500"
                                    />
                                }
                            >
                                Edit Growth Stage
                            </DropdownItem>
                            <DropdownItem
                                key="export"
                                onPress={() => {
                                    setExportRxOpen(true);
                                    prescriptionsQuery.mutate(
                                        {
                                            data: {
                                                simulation_ids: selectedFields
                                                    .map(
                                                        (field) =>
                                                            field
                                                                .default_nitrogen_plan_simulation
                                                                ?.id,
                                                    )
                                                    .filter(
                                                        (id): id is number =>
                                                            id !== undefined,
                                                    ),
                                            },
                                        },
                                        {
                                            onSuccess: (data) => {
                                                setPrescriptionsToExport(data);
                                            },
                                        },
                                    );
                                }}
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faFileZipper}
                                        className="text-primary-500"
                                    />
                                }
                            >
                                Export Rx
                            </DropdownItem>
                            <DropdownItem
                                key="log-application"
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faClipboardList}
                                        className="text-primary-500"
                                    />
                                }
                                onPress={() => setBulkLogApplicationOpen(true)}
                            >
                                Log Application
                            </DropdownItem>
                            <DropdownItem
                                key="log-irrigation"
                                startContent={
                                    <FontAwesomeIcon
                                        icon={faArrowUpFromGroundWater}
                                        className="text-primary-500"
                                    />
                                }
                                onPress={() => setBulkLogIrrigationOpen(true)}
                            >
                                Log Irrigation
                            </DropdownItem>
                        </DropdownMenu>
                    </Dropdown>
                </div>
            </div>
            <Divider />
            <TableV2
                data={tableData}
                columns={columns}
                sorting={sorting}
                setSorting={setSorting}
                manualSorting={true}
                isLoading={isLoading}
                onSelectionChange={handleSelectionChange}
                hasNextPage={query.hasNextPage}
                fetchNextPage={query.fetchNextPage}
                isFetchingNextPage={query.isFetchingNextPage}
                showEndMessage
                paddingBottom="20rem"
                pinnedColumns={["selection", "grower", "name"]}
                columnVisibility={columnVisibility}
                setColumnVisibility={handleColumnVisibilityChange}
                showScrollbar={true}
            />
            <>
                {bulkEditAction && (
                    <BulkEditNitrogenPlan
                        action={bulkEditAction}
                        fieldsWithPlan={selectedFields}
                        onFinish={() => {
                            setBulkEditAction(null);
                            query.refetch();
                        }}
                    />
                )}
                {exportRxOpen && prescriptionsToExport.length > 0 && (
                    <BulkRxExport
                        prescriptionsToExport={prescriptionsToExport.map(
                            (item) => ({
                                fieldId: item.field_id,
                                prescriptionId: item.prescription_id,
                                fieldName: item.field_name,
                            }),
                        )}
                        onFinish={() => setExportRxOpen(false)}
                    />
                )}
                {bulkSetGrowthStageOpen && (
                    <BulkSetGrowthStage
                        cropConfigurations={cropsQuery.data || []}
                        fieldIdsByCropConfigurationId={
                            selectedFieldsIdsByCropConfiguration
                        }
                        onFinish={() => setBulkSetGrowthStageOpen(false)}
                    />
                )}
                {editRecommendationOpen && (
                    <UserRecommendationForm
                        fieldIds={selectedFields.map((field) => field.id!)}
                        onClose={() => {
                            setEditRecommendationOpen(false);
                            query.refetch();
                        }}
                    />
                )}
            </>
            {advisorField && (
                <Modal
                    isOpen
                    onOpenChange={(isOpen) =>
                        setOpenAdvisorFieldId(isOpen ? advisorField : undefined)
                    }
                    classNames={{
                        closeButton: "mr-4",
                    }}
                    onClose={() => setOpenAdvisorFieldId(undefined)}
                    size="full"
                >
                    <ModalContent>
                        <ModalBody className="md:grid md:grid-cols-[1fr_auto_1fr] md:gap-8 flex flex-col  md:h-full overflow-y-scroll p-2">
                            {selectedField && (
                                <>
                                    <SIPreview selectedField={selectedField} />
                                    <Divider
                                        orientation="vertical"
                                        className="hidden md:block"
                                    />
                                    <FieldNitrogenAdvisor
                                        fieldId={advisorField.toString()}
                                        onClose={() =>
                                            setOpenAdvisorFieldId(undefined)
                                        }
                                        hideCloseButton
                                    />
                                </>
                            )}
                        </ModalBody>
                    </ModalContent>
                </Modal>
            )}
            {bulkLogIrrigationOpen && (
                <BulkLogIrrigation
                    fields={selectedFields}
                    onFinish={() => {
                        setBulkLogIrrigationOpen(false);
                        query.refetch();
                    }}
                />
            )}
            {bulkLogApplicationOpen && (
                <BulkLogApplication
                    fields={selectedFields}
                    onFinish={() => {
                        setBulkLogApplicationOpen(false);
                        query.refetch();
                    }}
                />
            )}
        </div>
    );
}
